import React, { useEffect, useMemo, useState } from "react";
import {
  BriefcaseBusiness,
  Camera,
  Save,
  UserRound,
} from "lucide-react";
import Bio from "./Bio";
import Experience from "./Experience";
import Gallery from "./Gallery";
import PortfolioImages from "./PortfolioImages";
import {
  PortfolioFormData,
  PortfolioProvider,
  usePortfolioContext,
} from "./PortfolioContext";
import "./UserPortfolioPage.css";

type PortfolioTab = "bio" | "gallery" | "experience";

const isFilled = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return Boolean(value);
};

const getCompletion = (formData: PortfolioFormData): number => {
  const checks: unknown[] = [
    formData.aboutMe,
    formData.height,
    formData.weight,
    formData.bust,
    formData.waist,
    formData.hips,
    formData.chest,
    formData.biceps,
    formData.hairType,
    formData.eyeColor,
    formData.skills,
    formData.knownLanguages,
    formData.locationPreference,
    formData.educationLevel,
    formData.degree,
    formData.contactName,
    formData.email,
    formData.mobile,
    formData.facebook,
    formData.instagram,
    formData.twitter,
    formData.youtube,
    formData.mainImage,
    formData.photos,
    formData.instagramReels,
    formData.youtubeVideos,
    formData.movieNames,
    formData.webseriesNames,
    formData.tvShowNames,
    formData.shortFilmNames,
    formData.adNames,
    formData.fashionEventNames,
  ];

  const filled = checks.filter(isFilled).length;
  return Math.round((filled / checks.length) * 100);
};

const PortfolioContent = () => {
  const {
    loading,
    error,
    success,
    message,
    myPortfolio,
    formData,
    clearPortfolioState,
    handleGlobalSave,
  } = usePortfolioContext();

  const [activeTab, setActiveTab] = useState<PortfolioTab>("bio");
  const completion = useMemo(() => getCompletion(formData), [formData]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        clearPortfolioState();
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [success, error, clearPortfolioState]);

  if (loading && !myPortfolio) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <section className="portfolio-page">
      <div className="portfolio-shell">
        <header className="portfolio-page-header">
          <div>
            <span className="portfolio-eyebrow">MY PROFILE</span>
            <h1>Portfolio</h1>
            <p>
              Build a complete, professional profile with your portfolio, bio,
              media gallery and experience.
            </p>
          </div>

          <div className="portfolio-completion-card">
            <span>Profile completion</span>
            <strong>{completion}%</strong>
            <div className="portfolio-progress">
              <span style={{ width: `${completion}%` }} />
            </div>
          </div>
        </header>

        {success && message && (
          <div className="portfolio-alert portfolio-alert-success" role="status">
            {message}
          </div>
        )}
        {error && (
          <div className="portfolio-alert portfolio-alert-error" role="alert">
            {error}
          </div>
        )}

        <PortfolioImages />

        <nav className="portfolio-tabs" aria-label="Portfolio sections">
          <button
            type="button"
            className={activeTab === "bio" ? "active" : ""}
            onClick={() => setActiveTab("bio")}
          >
            <UserRound size={18} />
            Bio
          </button>
          <button
            type="button"
            className={activeTab === "gallery" ? "active" : ""}
            onClick={() => setActiveTab("gallery")}
          >
            <Camera size={18} />
            Gallery
          </button>
          <button
            type="button"
            className={activeTab === "experience" ? "active" : ""}
            onClick={() => setActiveTab("experience")}
          >
            <BriefcaseBusiness size={18} />
            Experience
          </button>
        </nav>

        <div className="portfolio-content-card">
          {activeTab === "bio" && <Bio />}
          {activeTab === "gallery" && <Gallery />}
          {activeTab === "experience" && <Experience />}
        </div>

        <div className="portfolio-save-row">
          <button
            type="button"
            className="portfolio-primary-button"
            onClick={handleGlobalSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Portfolio
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

const Portfolio = () => {
  return (
    <PortfolioProvider>
      <PortfolioContent />
    </PortfolioProvider>
  );
};

export default Portfolio;
